export const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: '#4B6E82',  // Blue steel color for light mode
    },
    secondary: {
      main: '#6A9E72',  // Subtle green for contrast (to match the dark theme)
    },
    background: {
      default: '#F5F5F5',  // Softer light gray for a more serious tone
      paper: '#FFFFFF',    // White background for paper elements
    },
    text: {
      primary: '#1C1C1C',  // Darker gray for primary content
      secondary: '#666666', // Softer gray for secondary content
    },
    appBar: {
      main: '#F5F5F5',  // Softer light background for the AppBar
      contrastText: '#1C1C1C',  // Dark text on the AppBar
    },
    drawer: {
      main: '#FFFFFF',  // White background for the Drawer
      contrastText: '#1C1C1C',  // Dark text in the Drawer
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#4B6E82 #F0F0F0',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#F0F0F0',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#4B6E82',
            minHeight: 24,
            border: '3px solid #F0F0F0',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#6E8FA3',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#6E8FA3',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#6E8FA3',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#F0F0F0',
          },
        },
      },
    },
  },
};

export const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#4A7292',  // Blue steel color for dark mode (perfect)
    },
    secondary: {
      main: '#6A9E72',  // A subtle green to complement the blue
    },
    background: {
      default: '#151c23',
      paper: '#212830',
    },
    text: {
      primary: '#C9D1D9',  // Light gray text for primary content
      secondary: '#8B949E', // Gray text for secondary content
    },
    appBar: {
      main: '#151c23',  // Dark background for the AppBar
      contrastText: '#C9D1D9',  // Light gray text on the AppBar
    },
    drawer: {
      main: '#2a313d',  // Dark gray background for the Drawer
      contrastText: '#C9D1D9',  // Light gray text in the Drawer
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove any background images or gradients
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#424850 #2a313d',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#2a313d',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#424850',
            minHeight: 24,
            border: '3px solid #2a313d',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#5a5f66',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#5a5f66',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#5a5f66',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#2a313d',
          },
        },
      },
    },
  },
};

export const tokyonight = {
  palette: {
    mode: 'dark',  // Tokyonight theme is based on dark mode
    primary: {
      main: '#7aa2f7',  // Main blue color
    },
    secondary: {
      main: '#e0af68',  // Yellow for contrast
    },
    background: {
      default: '#1a1b26',  // Dark background for the app
      paper: '#292e42',  // Highlighted background for paper elements
    },
    text: {
      primary: '#c0caf5',  // Main text color (light blue)
      secondary: '#565f89', // Comment/secondary text color (gray)
    },
    appBar: {
      main: '#1a1b26',  // Dark app bar background
      contrastText: '#c0caf5',  // Light text on the app bar
    },
    drawer: {
      main: '#16161e',  // Dark drawer background
      contrastText: '#c0caf5',  // Light text in the drawer
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove any background images or gradients
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#7aa2f7 #1a1b26',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#1a1b26',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#7aa2f7',
            minHeight: 24,
            border: '3px solid #1a1b26',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#3d59a1',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#3d59a1',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#3d59a1',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#1a1b26',
          },
        },
      },
    },
  },
};

export const catppuccinmocha = {
  palette: {
    mode: 'dark', // Catppuccin Mocha is a dark theme
    primary: {
      main: '#89b4fa', // Blue from the Catppuccin palette
    },
    secondary: {
      main: '#f9e2af', // Yellow for contrast from the Catppuccin palette
    },
    background: {
      default: '#1e1e2e', // Base background color from Catppuccin Mocha
      paper: '#313244',    // Surface background for paper elements
    },
    text: {
      primary: '#cdd6f4',  // Main text color from Catppuccin palette
      secondary: '#9399b2', // Gray text for secondary content
    },
    appBar: {
      main: '#1e1e2e', // Dark background for the AppBar
      contrastText: '#cdd6f4',  // Light text for the AppBar
    },
    drawer: {
      main: '#181825',  // Darker background for the Drawer
      contrastText: '#cdd6f4',  // Light text in the Drawer
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove any background images or gradients
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#89b4fa #1e1e2e',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#1e1e2e',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#89b4fa',
            minHeight: 24,
            border: '3px solid #1e1e2e',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#74c7ec',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#74c7ec',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#74c7ec',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#1e1e2e',
          },
        },
      },
    },
  },
};

export const heisenberg = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#32c9fa', // Crystal blue for primary elements
    },
    secondary: {
      main: '#009669', // Barium green for secondary highlights
    },
    background: {
      default: '#000000', // Black background
      paper: '#121212',   // Dark gray for surfaces
    },
    text: {
      primary: '#cccccc', // Light gray text for readability
      secondary: '#5c6773', // Muted gray text for secondary content
    },
    appBar: {
      main: '#000000', // Black AppBar background
      contrastText: '#32c9fa',  // Crystal blue text on AppBar
    },
    drawer: {
      main: '#121212',  // Dark gray background for the Drawer
      contrastText: '#cccccc',  // Light gray text in the Drawer
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove any background images or gradients
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#32c9fa #000000',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#000000',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#32c9fa',
            minHeight: 24,
            border: '3px solid #000000',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#009669',
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#009669',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#009669',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#000000',
          },
        },
      },
    },
  },
};

export const colibri = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#ECCDBA',  // good
    },
    secondary: {
      main: '#a4a0e8', // ??
    },
    background: {
      default: '#281733', // good
      paper: '#452859', // good
    },
    text: {
      primary: '#ebeafa',  // Lilac for default text
      secondary: '#dbbfef', // Silver for secondary text
    },
    // headings: {
    //   main: '#EFBA5D', // Amber for headings
    // },
    appBar: {
      main: '#281733',
      contrastText: '#ebeafa',
    },
    drawer: {
      main: '#452859',
      contrastText: '#ebeafa',  // Lilac for Drawer text
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#ECCDBA #281733',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#281733',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#f3f2fc',
            minHeight: 24,
            border: '3px solid #281733',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#dbbfef',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#dbbfef',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#281733',
          },
        },
      },
    },
  },
};

export const everforest = {
  palette: {
    mode: 'dark', // Everforest is a dark theme
    primary: {
      main: '#a7c080', // Green for primary highlights
    },
    secondary: {
      main: '#83c092', // Aqua for secondary elements
    },
    background: {
      default: '#2d353b', // Default Background (bg0)
      paper: '#343f44',   // Slightly lighter background for surfaces (bg1)
    },
    text: {
      primary: '#d3c6aa', // Default Foreground
      secondary: '#859289', // Muted grey for secondary text (grey1)
    },
    appBar: {
      main: '#2d353b', // Background for AppBar (bg0)
      contrastText: '#d3c6aa',  // Text color for AppBar (fg)
    },
    drawer: {
      main: '#343f44',  // Background for the Drawer (bg1)
      contrastText: '#d3c6aa',  // Text color in the Drawer (fg)
    },
    error: {
      main: '#e67e80', // Red for errors
    },
    warning: {
      main: '#e69875', // Orange for warnings
    },
    info: {
      main: '#83c092', // Aqua for informational text
    },
    success: {
      main: '#a7c080', // Green for success
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.appBar.main,
          color: theme.palette.appBar.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.drawer.main,
          color: theme.palette.drawer.contrastText,
          backgroundImage: 'none',
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',  // Remove any background images or gradients
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarColor: '#a7c080 #2d353b',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: '#2d353b',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#a7c080', // Green scrollbar thumb
            minHeight: 24,
            border: '3px solid #2d353b',
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: '#83c092', // Aqua for focus state
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: '#83c092',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#83c092',
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: '#2d353b',
          },
        },
      },
    },
  },
  // typography: {
  //   fontFamily: 'Monospace, sans-serif', // Apply custom font globally
  //   allVariants: {
  //     color: '#d3c6aa', // Default text color
  //   },
  // },
};
